import React from 'react';
import { motion } from 'framer-motion';
type AnimatedDataFlowProps = {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  color?: string;
  delay?: number;
  duration?: number;
  active?: boolean;
  bidirectional?: boolean;
  label?: string;
  straight?: boolean;
  flowType?: 'data' | 'energy';
  dashed?: boolean;
};
export function AnimatedDataFlow({
  startX,
  startY,
  endX,
  endY,
  color = '#3b82f6',
  delay = 0,
  duration = 2,
  active = true,
  bidirectional = false,
  label,
  straight = false,
  flowType = 'data',
  dashed = false
}: AnimatedDataFlowProps) {
  const pathId = `path-${startX}-${startY}-${endX}-${endY}`;
  // Calculate path - straight or curved
  let pathD: string;
  let reversePathD: string;
  if (straight) {
    // Straight line
    pathD = `M ${startX} ${startY} L ${endX} ${endY}`;
    reversePathD = `M ${endX} ${endY} L ${startX} ${startY}`;
  } else {
    // Curved path
    const midX = (startX + endX) / 2;
    const midY = (startY + endY) / 2;
    const dx = endX - startX;
    const dy = endY - startY;
    // Calculate control point for curve
    // For vertical connections (dx is small), curve horizontally
    // For horizontal connections (dy is small), curve vertically
    const isVertical = Math.abs(dy) > Math.abs(dx);
    let perpX, perpY;
    if (isVertical) {
      perpX = dx * 0.2; // Slight curve
      perpY = 0;
    } else {
      perpX = 0;
      perpY = dy * 0.2; // Slight curve
    }
    // If it's a diagonal or complex path, use the original logic
    if (Math.abs(dx) > 50 && Math.abs(dy) > 50) {
      perpX = -dy * 0.2;
      perpY = dx * 0.2;
    }
    pathD = `M ${startX} ${startY} Q ${midX + perpX} ${midY + perpY} ${endX} ${endY}`;
    reversePathD = `M ${endX} ${endY} Q ${midX + perpX} ${midY + perpY} ${startX} ${startY}`;
  }
  // Style differences for energy vs data flows
  const strokeWidth = flowType === 'energy' ? 3 : 2;
  const pulseSize = flowType === 'energy' ? 8 : 6;
  const glowIntensity = flowType === 'energy' ? 5 : 3;
  return <g data-id="element-1">
      {/* Base path line */}
      <motion.path d={pathD} fill="none" stroke={color} strokeWidth={strokeWidth} strokeOpacity={flowType === 'energy' ? 0.4 : 0.3} strokeDasharray={dashed ? '6 4' : 'none'} initial={{
      pathLength: 0
    }} animate={{
      pathLength: active ? 1 : 0
    }} transition={{
      duration: 0.8,
      delay
    }} data-id="element-2" />

      {/* Glowing animated pulse - forward direction */}
      {active && <>
          <defs data-id="element-3">
            <linearGradient id={`gradient-${pathId}`} x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-4">
              <stop offset="0%" stopColor={color} stopOpacity="0" data-id="element-5" />
              <stop offset="50%" stopColor={color} stopOpacity="1" data-id="element-6" />
              <stop offset="100%" stopColor={color} stopOpacity="0" data-id="element-7" />
            </linearGradient>
            <filter id={`glow-${pathId}`} x="-50%" y="-50%" width="200%" height="200%" data-id="element-8">
              <feGaussianBlur stdDeviation={glowIntensity} result="coloredBlur" data-id="element-9" />
              <feMerge data-id="element-10">
                <feMergeNode in="coloredBlur" data-id="element-11" />
                <feMergeNode in="SourceGraphic" data-id="element-12" />
              </feMerge>
            </filter>
          </defs>

          {/* Animated dot traveling along path */}
          <motion.circle r={pulseSize} fill={color} filter={`url(#glow-${pathId})`} initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 1, 1, 0]
      }} transition={{
        duration: duration,
        delay: delay,
        repeat: Infinity,
        repeatDelay: flowType === 'energy' ? 0.5 : 1,
        times: [0, 0.1, 0.9, 1]
      }} data-id="element-13">
            <animateMotion dur={`${duration}s`} repeatCount="indefinite" begin={`${delay}s`} path={pathD} data-id="element-14" />
          </motion.circle>

          {/* Second pulse for continuous flow effect */}
          <motion.circle r={pulseSize - 2} fill={color} filter={`url(#glow-${pathId})`} initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 0.7, 0.7, 0]
      }} transition={{
        duration: duration,
        delay: delay + duration / 2,
        repeat: Infinity,
        repeatDelay: flowType === 'energy' ? 0.5 : 1,
        times: [0, 0.1, 0.9, 1]
      }} data-id="element-15">
            <animateMotion dur={`${duration}s`} repeatCount="indefinite" begin={`${delay + duration / 2}s`} path={pathD} data-id="element-16" />
          </motion.circle>

          {/* Reverse direction for bidirectional */}
          {bidirectional && <>
              <motion.circle r={pulseSize - 1} fill={color} filter={`url(#glow-${pathId})`} initial={{
          opacity: 0
        }} animate={{
          opacity: [0, 0.8, 0.8, 0]
        }} transition={{
          duration: duration,
          delay: delay + 0.5,
          repeat: Infinity,
          repeatDelay: flowType === 'energy' ? 0.5 : 1.5,
          times: [0, 0.1, 0.9, 1]
        }} data-id="element-17">
                <animateMotion dur={`${duration}s`} repeatCount="indefinite" begin={`${delay + 0.5}s`} path={reversePathD} data-id="element-18" />
              </motion.circle>
            </>}
        </>}

      {/* Label */}
      {label && <motion.text x={(startX + endX) / 2} y={(startY + endY) / 2 - 10} fill={color} fontSize="10" textAnchor="middle" initial={{
      opacity: 0
    }} animate={{
      opacity: active ? 0.8 : 0
    }} transition={{
      delay: delay + 0.5
    }} data-id="element-19">
          {label}
        </motion.text>}
    </g>;
}